<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'ExchangeRateDateRuleFields',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'update:modelValue']);

  const props = defineProps({
    ...userComponentProps,
    modelValue: {
      type: Object,
      required: false,
    },
    readonly: {
      type: Boolean,
      required: false,
    },
    required: {
      type: Boolean,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const offsetDays = ref(null);

  const $refs = {
    get offsetDays() {
      return offsetDays.value;
    },
  };

  const addRule = (...args) => {
    const [id] = args;
    const selectedRule = _.find(rules.value, { id });
    if (selectedRule) {
      _.extend(item.value, selectedRule.rule);
      setData('selectedRuleID', null);
    }
    emit('update:modelValue', item.value);
  };

  const item = ref(null);
  const labelAlign = ref('right');
  const rule_type = ref('exchange_rate_date');
  const rules = ref(null);
  const metadata = ref(null);
  const selectedRuleID = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    item: item.value,
    labelAlign: labelAlign.value,
    rule_type: rule_type.value,
    rules: rules.value,
    metadata: metadata.value,
    selectedRuleID: selectedRuleID.value,
    ux: ux.value,
  }));

  const $$load_item = () => {
    return props.modelValue || {};
  };
  const $$load_rules = () => {
    return $app.value.repo
      .get('list_object_by_type', {
        parameters: { object_type: 'billing-rule' },
      })
      .then((items) =>
        _.chain(items).filter({ rule_type: rule_type.value }).value()
      );
  };
  const $$load_metadata = () => {
    return $app.value.repo.get('ui_metadata');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'item' },
      { name: 'labelAlign' },
      { name: 'rule_type' },
      { name: 'rules' },
      { name: 'metadata' },
      { name: 'selectedRuleID' },
      { name: 'ux' },
    ],
    loaders: {
      item: $$load_item,
      rules: $$load_rules,
      metadata: $$load_metadata,
    },
    ux: {},
    dataRefs: {
      item,
      labelAlign,
      rule_type,
      rules,
      metadata,
      selectedRuleID,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const referenceMonthOptions = computed(() => {
    if (loaded.value) {
      return metadata.value.exchange_rate_date_reference_month_options;
    }
    return null;
  });
  const daySpecifierOptions = computed(() => {
    if (loaded.value) {
      return metadata.value.exchange_rate_date_day_specifier_options;
    }
    return null;
  });

  watch(
    () => props.modelValue,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modelValue');
    }
  );
  watch(
    () => props.readonly,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('readonly');
    }
  );
  watch(
    () => props.required,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('required');
    }
  );

  const act_6705964765214521_script_lhkbwj0dj_value_callable = async (
    $event
  ) => {
    return addRule(selectedRuleID.value);
  };
  const act_6705964765214521_script_lhkbwj0dj_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_6705964765214521_script_lhkbwj0dj_value_callable($event);
      },
    };
  };

  const act_6705964765214521_script_lhkbwj0dj = new actionTypes.ScriptAction({
    actionArgs: act_6705964765214521_script_lhkbwj0dj_getActionArgs,
  });
  const inst_evh_6705964765214521_click_pq9sp1xyx =
    new actionTypes.EventHandlerImpl({
      action: act_6705964765214521_script_lhkbwj0dj,
      event: 'click',
      displayName: 'script',
    });
  const inst_evh_6705964765214521_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_6705964765214521_click_pq9sp1xyx],
    event: 'click',
  });

  const actions = {
    inst_evh_6705964765214521_click_pq9sp1xyx,
    inst_evh_6705964765214521_click,
  };

  const evh_6705964765214521_click_pq9sp1xyx = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click_pq9sp1xyx.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_6705964765214521_click = async (event, scope) => {
    return await actions.inst_evh_6705964765214521_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <div class="row mb-3" v-if="loaded">
    <div class="col" v-if="!readonly">
      <div class="input-group">
        <span class="input-group-text">
          <ItpIcon icon="material-symbols:calendar-month"> </ItpIcon>
        </span>
        <ItpFormNativeSelect
          name="selectedRuleID"
          v-model="selectedRuleID"
          :options="rules"
          valueField="id"
          textField="rule.description"
          :placeholder="
            pgettext('BillingDateRule', 'Select predefined rule to add.')
          "
        >
        </ItpFormNativeSelect>
        <ItpButton
          variant="light"
          icon="fa-plus"
          :tooltip="pgettext('BillingDateRule', 'Add rule configuration')"
          :disabled="!selectedRuleID"
          @click="evh_6705964765214521_click($event, {})"
        >
        </ItpButton>
      </div>
    </div>
  </div>
  <div class="row mb-3" v-if="loaded">
    <div class="col-md-5 form-group">
      <ItpFormNativeSelect
        name="reference_month"
        v-model="item.reference_month"
        :options="referenceMonthOptions"
        valueField="value"
        textField="name"
        :required="required"
        :readonly="readonly"
      >
      </ItpFormNativeSelect>
    </div>
    <div class="col-md-4 form-group">
      <ItpFormNativeSelect
        name="day_specifier"
        v-model="item.day_specifier"
        :options="daySpecifierOptions"
        valueField="value"
        textField="name"
        :required="required"
        :readonly="readonly"
      >
      </ItpFormNativeSelect>
    </div>
    <div class="col-md-3">
      <div class="input-group">
        <ItpFormInput
          type="number"
          name="day_value"
          v-model="item.day_value"
          :readonly="readonly"
          ref="offsetDays"
        >
        </ItpFormInput>
        <span class="input-group-text">
          {{ pgettext('InputGroupAddon', 'day') }}
        </span>
      </div>
    </div>
  </div>
  <div class="row" v-if="loaded">
    <div class="col">
      <ItpFormInput
        name="description"
        v-model="item.description"
        :placeholder="pgettext('ExchangeRateDateRule', 'Rule description')"
        :readonly="readonly"
      >
      </ItpFormInput>
    </div>
  </div>
</template>
